import { memo, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Trophy } from 'lucide-react';
import type { Move, GameVariant, DrawnOpeningInfo } from '@draughts/shared';
import { useChatStore } from '../../store/chat-store';
import { useGameSpectators } from '../../store/spectators-store';
import MoveList from './MoveList';
import ReviewControls from './ReviewControls';
import ChatPanel from '../online/ChatPanel';
import SpectatorsPanel from './SpectatorsPanel';
import './GameSidebarTabs.css';

type TabId = 'moves' | 'chat' | 'standings' | 'spectators';

export interface GameSidebarTabsProps {
  /**
   * Moves to list. Omit for an online game, whose board lives in the multiplayer
   * store MoveList reads by default; pass them when the board belongs to another
   * store — an arena or tournament game — or the list renders empty.
   */
  moves?: Move[];
  /** Variant the notation is formatted for. Same defaulting as `moves`. */
  variant?: GameVariant;
  /** L'apertura sorteggiata, per l'intestazione della scheda Mosse. */
  opening?: DrawnOpeningInfo | null;
  /**
   * Le semi-mosse imposte dal sorteggio, per il «vai all'inizio» di ReviewControls.
   *
   * IL NUMERO, non `opening.moves.length`: nel rientro il conto arriva dal server
   * (`data.openingPlyCount ?? data.opening?.moves?.length ?? 0`) ed e' lui
   * l'autorevole, mentre l'apertura da mostrare puo' essere rimasta `null`. E deve
   * essere lo stesso che il contenitore passa a `GameScreen` per la barra mobile.
   */
  openingPlyCount?: number;
  /**
   * La classifica del torneo, come terza scheda. Assente fuori dal torneo, e la
   * scheda non si disegna: partita online, arena e spettatore restano come sono.
   *
   * Di SOLA ICONA, come l'occhio: accanto a «Chat» e «Mosse» una terza parola
   * non entra: la colonna e' ~314px, l'occhio se ne prende 44, e a tre schede
   * di testo resterebbero ~89px di cella l'una — cioe' ~57 di testo, togliendo
   * i due padding. «Classifica» o «Klassement» non ci stanno.
   *
   * Un elemento STABILE: questo componente e' `memo` perche' l'orologio fa
   * ridisegnare chi lo monta piu' volte al secondo.
   */
  standings?: ReactNode;
  /**
   * Chiede di aprire la scheda della classifica: ogni volta che il numero
   * CAMBIA. E' il punteggio nell'intestazione del torneo a chiederlo, e un
   * numero invece di un booleano fa si' che il secondo tocco — dopo che si e'
   * passati a Mosse — la riapra ancora.
   */
  standingsRequest?: number;
}

function GameSidebarTabs({ moves, variant, opening, openingPlyCount = 0, standings, standingsRequest = 0 }: GameSidebarTabsProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabId>('moves');
  const unreadCount = useChatStore((state) => state.unreadCount);
  const setChatActive = useChatStore((state) => state.setActive);
  const { count: spectatorCount } = useGameSpectators();

  // Tell the store whether the chat is the visible tab. With the Moves tab
  // active (the default) chat counts as closed, so incoming opponent messages
  // increment unreadCount and the badge below shows. Switching to Chat clears it.
  useEffect(() => {
    setChatActive(activeTab === 'chat');
  }, [activeTab, setChatActive]);

  useEffect(() => {
    if (standingsRequest > 0 && standings) setActiveTab('standings');
    // La richiesta e' il numero: `standings` e' stabile e non deve riaprirla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standingsRequest]);

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
  };

  /* Il contenuto della scheda aperta. Era una catena di ternari, che a quattro
     rami non si legge piu'.
     Mosse e' l'ULTIMO ramo, non il primo, perche' e' anche il ripiego: se la
     classifica sparisse da sotto la scheda aperta si vedrebbero le mosse, non la
     chat come prima — e Mosse e' la scheda di partenza. */
  const contenutoScheda = () => {
    if (activeTab === 'chat') return <ChatPanel />;
    if (activeTab === 'spectators') return <SpectatorsPanel />;
    if (activeTab === 'standings' && standings) return standings;
    return (
      <>
        <ReviewControls variant="panel" openingPlyCount={openingPlyCount} />
        <MoveList moves={moves} variant={variant} opening={opening} />
      </>
    );
  };

  return (
    <div className="sidebar-tabs" data-testid="sidebar-tabs">
      {/* La `tablist` è il gruppo INTERNO, non la riga: il gruppo si spartisce lo
          spazio fra le schede, la riga no. Ci stanno DENTRO tutte, l'occhio
          compreso — da quando mostra i nomi nella colonna invece di aprire un
          pannello a tutto schermo è una scheda come le altre, e un `role="tab"`
          fuori dalla sua `tablist` è malformato per chi usa uno screen reader. */}
      <div className="sidebar-tabs-bar">
        <div className="sidebar-tabs-group" role="tablist">
          <button
            className={`sidebar-tab${activeTab === 'chat' ? ' active' : ''}`}
            onClick={() => handleTabChange('chat')}
            role="tab"
            aria-selected={activeTab === 'chat'}
            data-testid="tab-chat"
            type="button"
          >
            {t('game.sidebar.chat')}
            {activeTab !== 'chat' && unreadCount > 0 && (
              <span className="sidebar-tab-badge" data-testid="chat-unread-badge">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            className={`sidebar-tab${activeTab === 'moves' ? ' active' : ''}`}
            onClick={() => handleTabChange('moves')}
            role="tab"
            aria-selected={activeTab === 'moves'}
            data-testid="tab-moves"
            type="button"
          >
            {t('game.sidebar.moves')}
          </button>
          {standings && (
            <button
              className={`sidebar-tab-icon${activeTab === 'standings' ? ' active' : ''}`}
              onClick={() => handleTabChange('standings')}
              role="tab"
              aria-selected={activeTab === 'standings'}
              aria-label={t('tournament.standings.title')}
              title={t('tournament.standings.title')}
              data-testid="tab-standings"
              type="button"
            >
              <Trophy size={16} aria-hidden="true" />
            </button>
          )}
          {/* Sempre presente, anche a zero: sapere di non essere guardati è
              un'informazione quanto il contrario. Di sola icona come la coppa, e
              per la stessa ragione di spazio (vedi il commento di `standings`).
              Il numero NON è un «non letti»: resta anche a scheda aperta. */}
          <button
            className={`sidebar-tab-icon${activeTab === 'spectators' ? ' active' : ''}`}
            onClick={() => handleTabChange('spectators')}
            role="tab"
            aria-selected={activeTab === 'spectators'}
            aria-label={t('game.sidebar.spectators')}
            title={t('game.sidebar.spectators')}
            data-testid="tab-spectators"
            type="button"
          >
            <Eye size={16} aria-hidden="true" />
            {spectatorCount > 0 && (
              <span className="sidebar-tab-badge" data-testid="spectators-badge">
                {spectatorCount}
              </span>
            )}
          </button>
        </div>
      </div>
      <div className="sidebar-tabs-content" role="tabpanel">{contenutoScheda()}</div>
    </div>
  );
}

export default memo(GameSidebarTabs);
